/**
 * Handoff du panier entre l'iframe widget et la page top `/checkout`.
 *
 * Pourquoi ce module existe-t-il ?
 * Les navigateurs modernes (Chrome 115+ avec DOM Storage Partitioning,
 * Firefox avec Total Cookie Protection, Safari avec ITP) **partitionnent**
 * le `localStorage` des iframes par la paire `(top-level site, frame
 * origin)`. Quand `EmbedBookingPage` (servie depuis
 * `rintintin-booking.com` dans une iframe d'un site tiers) écrit dans
 * `localStorage['rintintin_cart']`, l'écriture va dans un bucket
 * partitionné. Le redirect ensuite vers `rintintin-booking.com/checkout`
 * en TOP-level lit le bucket NON partitionné (first-party) → vide.
 *
 * Solution : sérialiser l'item ajouté dans le **fragment** de l'URL
 * (`#cart=<base64url>`). Le fragment :
 *  - n'est JAMAIS envoyé au serveur → pas de leak dans les logs Netlify /
 *    Sentry / proxies,
 *  - est accessible via `window.location.hash` côté client uniquement,
 *  - survit au redirect cross-origin (contrairement au `localStorage`).
 *
 * Côté checkout, `CartContext.seedFromHandoff` consomme le hash, seed le
 * panier (avec dédup) puis nettoie `window.location.hash` pour éviter le
 * re-seed lors d'une navigation interne ultérieure.
 */
import type { CartItem } from '../contexts/CartContext';
import { isCompleteGiftCode, normalizeGiftCode, stashPendingGiftCode } from './giftVoucherCode';

/** Préfixe du fragment d'URL transportant le panier. */
export const CART_HANDOFF_HASH_KEY = 'cart';

/** Code cadeau saisi dans l’iframe (sessionStorage partitionné, comme le panier). */
export const GIFT_HANDOFF_HASH_KEY = 'gift';

/** Limite de sécurité : un panier handoff au-delà de cette taille (octets
 * encodés) est rejeté pour éviter de polluer l'URL et de heurter les limites
 * navigateurs (~2 Mo en pratique, mais on reste très en deçà). */
const MAX_HANDOFF_ENCODED_BYTES = 16 * 1024; // 16 KiB

/** Encode UTF-8 → base64url (sans padding, sans `+` ni `/`). */
function base64UrlEncode(input: string): string {
  // btoa ne supporte que du Latin-1 → on passe par TextEncoder + binary string.
  const bytes = new TextEncoder().encode(input);
  let binary = '';
  for (let i = 0; i < bytes.length; i += 1) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** Décode base64url → UTF-8. Retourne `null` si l'entrée est invalide. */
function base64UrlDecode(input: string): string | null {
  try {
    const padded = input.replace(/-/g, '+').replace(/_/g, '/');
    const padLen = (4 - (padded.length % 4)) % 4;
    const binary = atob(padded + '='.repeat(padLen));
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) {
      bytes[i] = binary.charCodeAt(i);
    }
    return new TextDecoder().decode(bytes);
  } catch {
    return null;
  }
}

/**
 * Construit le fragment URL `#cart=<base64url>` à append à l'URL de checkout.
 * Retourne une chaîne vide si l'encodage échoue ou dépasse la limite — dans
 * ce cas le caller redirige sans handoff, le panier sera vide côté checkout
 * (dégradation gracieuse plutôt qu'erreur dure).
 */
export function buildCartHandoffHash(item: Omit<CartItem, 'cartItemId'>): string {
  try {
    const json = JSON.stringify(item);
    const encoded = base64UrlEncode(json);
    if (encoded.length > MAX_HANDOFF_ENCODED_BYTES) return '';
    return `#${CART_HANDOFF_HASH_KEY}=${encoded}`;
  } catch {
    return '';
  }
}

function hashWithoutKeys(hash: string, keys: string[]): string {
  const trimmed = hash.startsWith('#') ? hash.slice(1) : hash;
  if (!trimmed) return '';
  const drop = new Set(keys);
  const kept = trimmed.split('&').filter(part => {
    if (!part) return false;
    const eq = part.indexOf('=');
    const name = decodeURIComponent(eq === -1 ? part : part.slice(0, eq));
    return !drop.has(name);
  });
  return kept.length ? `#${kept.join('&')}` : '';
}

/** Ajoute le code cadeau au fragment sans ré-encoder le panier. */
export function withGiftHandoffHash(hash: string, giftCode?: string | null): string {
  const code = normalizeGiftCode(giftCode || '');
  if (!isCompleteGiftCode(code)) return hash || '';
  if (readGiftHandoffFromHash(hash) === code) return hash || '';
  const withoutGift = hashWithoutKeys(hash, [GIFT_HANDOFF_HASH_KEY]);
  const giftPart = `${GIFT_HANDOFF_HASH_KEY}=${encodeURIComponent(code)}`;
  if (!withoutGift) return `#${giftPart}`;
  return `${withoutGift}&${giftPart}`;
}

/** Pose le code cadeau sur n’importe quelle URL de sortie widget (forfait, crédits, checkout). */
export function appendGiftHandoffToUrl(url: string, giftCode?: string | null): string {
  const code = normalizeGiftCode(giftCode || '');
  if (!isCompleteGiftCode(code)) return url;
  const hashIdx = url.indexOf('#');
  const base = hashIdx === -1 ? url : url.slice(0, hashIdx);
  const existingHash = hashIdx === -1 ? '' : url.slice(hashIdx);
  return `${base}${withGiftHandoffHash(existingHash, code)}`;
}

/** Copie le code du fragment vers sessionStorage first-party, sans toucher au panier. */
export function consumeGiftHandoffFromUrl(): string {
  const code = readGiftHandoffFromHash();
  if (!code) return '';
  stashPendingGiftCode(code);
  if (typeof window === 'undefined' || typeof window.history === 'undefined') return code;
  const nextHash = hashWithoutKeys(window.location.hash, [GIFT_HANDOFF_HASH_KEY]);
  if (nextHash === (window.location.hash || '')) return code;
  const nextUrl = window.location.pathname + window.location.search + nextHash;
  window.history.replaceState(window.history.state, '', nextUrl);
  return code;
}

export function readGiftHandoffFromHash(hashSource?: string): string {
  const hash = hashSource ?? (typeof window !== 'undefined' ? window.location.hash : '');
  if (!hash) return '';
  const trimmed = hash.startsWith('#') ? hash.slice(1) : hash;
  const params = new URLSearchParams(trimmed);
  const code = normalizeGiftCode(params.get(GIFT_HANDOFF_HASH_KEY) || '');
  return isCompleteGiftCode(code) ? code : '';
}

/**
 * Lit `window.location.hash` (ou la chaîne fournie en test) et extrait l'item
 * panier sérialisé. Retourne `null` si pas de handoff valide.
 *
 * NOTE : la fonction NE consomme PAS le hash — c'est au caller (CartContext)
 * de nettoyer l'URL après seed via `history.replaceState`. On garde cette
 * séparation pour rester testable et idempotente.
 */
export function readCartHandoffFromHash(hashSource?: string): Omit<CartItem, 'cartItemId'> | null {
  const hash = hashSource ?? (typeof window !== 'undefined' ? window.location.hash : '');
  if (!hash) return null;

  // hash commence par '#' — on retire ce caractère puis on parse comme une
  // query string (`cart=...&other=...`) pour rester compatible si on ajoute
  // d'autres clés de handoff plus tard.
  const trimmed = hash.startsWith('#') ? hash.slice(1) : hash;
  const params = new URLSearchParams(trimmed);
  const encoded = params.get(CART_HANDOFF_HASH_KEY);
  if (!encoded) return null;

  const decoded = base64UrlDecode(encoded);
  if (!decoded) return null;

  try {
    const parsed = JSON.parse(decoded);
    if (!parsed || typeof parsed !== 'object') return null;
    // Validation minimale : on exige au moins proId + serviceId numériques
    // pour éviter d'injecter un objet inutilisable dans le panier.
    if (typeof parsed.proId !== 'number') return null;
    return parsed as Omit<CartItem, 'cartItemId'>;
  } catch {
    return null;
  }
}

/**
 * Retire les clés `cart` et `gift` du fragment d'URL courant sans recharger
 * la page. Conserve les éventuelles autres clés du hash.
 * No-op si on n'est pas dans un environnement navigateur.
 */
export function clearCartHandoffFromHash(): void {
  if (typeof window === 'undefined' || typeof window.history === 'undefined') return;
  const hash = window.location.hash;
  if (!hash) return;
  const nextHash = hashWithoutKeys(hash, [CART_HANDOFF_HASH_KEY, GIFT_HANDOFF_HASH_KEY]);
  if (nextHash === hash) return;
  const nextUrl = window.location.pathname + window.location.search + nextHash;
  window.history.replaceState(window.history.state, '', nextUrl);
}
